/**
 * EmailFlowsManager (Super Admin)
 *
 * Every email Worship OS sends, as an editable template. What you save here is
 * the base email for every church: church-level content (names, links, the
 * intake answers, a track's interview wording) arrives as {{variables}} at
 * send time and can never be overridden from here.
 *
 * Backend: /api/admin/email-flows (list, get, put, reset, preview, test,
 * versions, restore). The server owns the facts (trigger, recipients, sender,
 * variables); this screen owns the words, the on/off switch, and the waits
 * between the emails of a sequence.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Clock, ChevronRight, Save, RotateCcw, History, Send, Search,
  Plus, Trash2, Eye, Code, Type, AlertTriangle, ExternalLink, Users, Bold, Italic,
  Underline, List, ListOrdered, Heading1, Heading2, Link2, Copy, Check, Loader2,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useDesign } from '@/contexts/DesignContext';
import StudioToolHeader, { ToolSearch } from '@/components/studio/admin/StudioToolHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_LABEL = { event: 'Sent when something happens', scheduled: 'Sent by a daily schedule', manual: 'Sent by a super-admin' };
const AUDIENCE_LABEL = {
  candidate: 'Candidate', invitee: 'Person invited', member: 'New team member', admins: 'Church admins',
  owner: 'Church owner', billing_admin: 'Billing admin', members: 'Whole team', support: 'Worship OS support',
  all_admins: 'Every church admin',
};
const RECIPIENT_LABEL = { candidate: 'the candidate', invitee: 'the person invited', admins: 'the admin who invited them' };

const shell = 'min-h-screen bg-gradient-to-br from-slate-900 to-slate-800';
const panel = 'bg-slate-800/60 border border-slate-700 rounded-xl';
const label = 'text-[11px] font-semibold uppercase tracking-wider text-slate-400';
/* Studio (§31): classic paints this whole tool a hard-coded slate-900 navy
   whatever the theme (114 §14.17, mock question 2). Studio swaps the three
   shell strings for the app tokens so it follows the theme like every other
   tool; the classic strings above are untouched. Mock frames 11-14. */
const shellSt = 'st31-page st31-flowshell';
const panelSt = 'st31-card';
const labelSt = 'st31-lbl';

async function api(path, { method = 'GET', body, userEmail } = {}) {
  const res = await fetch(`${API_URL}/api/admin/email-flows${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail
      : Array.isArray(data?.detail) ? data.detail.map((d) => d?.msg || '').filter(Boolean).join(' ') : '';
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return data;
}

// True when the body carries markup the simple editor cannot round-trip.
// `{{variable}}` counts: Post & Email's whole body is one variable, and the
// simple editor would wrap it in a paragraph.
const needsHtmlMode = (html) => /<table|style=|<div|<h[1-6]|<!--|^\s*\{\{/i.test(html || '');
const emailCount = (flow) => (flow.steps || []).filter((s) => s.type === 'email').length;

/** "Day 4 · Day 8 · Day 12 (admin)" from the steps of a sequence. */
function scheduleSummary(flow) {
  let day = 0; const out = [];
  for (const s of flow.steps || []) {
    if (s.type === 'delay') day += Math.max(0, parseInt(s.delay_days, 10) || 0);
    else if (s.type === 'email') out.push({ day, recipient: s.recipient });
  }
  return out;
}

function StatusChip({ enabled }) {
  const { isStudio } = useDesign();
  if (isStudio) {
    // Off is not a failure, so it keeps the neutral chip (mock question 4).
    return <span className={`st-chip st-tb-xs${enabled ? ' st-tb-ok' : ''}`}>{enabled ? 'On' : 'Off'}</span>;
  }
  return enabled
    ? <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15">On</Badge>
    : <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/15">Off</Badge>;
}

// ─────────────────────────────────────────── rich text editor (TipTap)
function ToolbarButton({ onClick, isActive, children, title }) {
  const { isStudio } = useDesign();
  if (isStudio) {
    return (
      <button type="button" onClick={onClick} title={title} className={`st-ibtn${isActive ? ' st31-tbon' : ''}`} style={{ width: 28, height: 28 }}>
        {children}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} title={title}
      className={`p-1.5 rounded hover:bg-slate-200 dark:hover:bg-white/10 transition-colors ${isActive ? 'bg-slate-200 dark:bg-white/10 text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-300'}`}>
      {children}
    </button>
  );
}

function RichEditor({ content, onChange }) {
  const { isStudio } = useDesign();
  // TipTap binds onUpdate once, at construction: route it through a ref so it
  // always calls the latest onChange (a stale closure reverted sibling edits).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] }, link: { openOnClick: false } }),
    ],
    content: content || '',
    onUpdate: ({ editor: ed }) => onChangeRef.current(ed.getHTML()),
  });
  useEffect(() => {
    if (editor && typeof content === 'string' && editor.getHTML() !== content) editor.commands.setContent(content);
  }, [editor, content]);
  const addLink = () => {
    const url = window.prompt('Link URL (you can use a variable such as {{invite_link}}):');
    if (url && editor) editor.chain().focus().setLink({ href: url }).run();
  };
  if (!editor) return <div className="p-4 text-slate-400">Loading editor…</div>;
  return (
    <div className={isStudio ? 'st31-rte' : 'border border-slate-600 rounded-lg overflow-hidden bg-white text-slate-900'}>
      <div className={isStudio ? 'st31-tbstrip' : 'flex flex-wrap items-center gap-1 p-2 border-b border-slate-200 bg-slate-50'}>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 className="w-4 h-4" /></ToolbarButton>
        <div className={isStudio ? 'st31-div' : 'w-px h-5 bg-slate-300 mx-1'} />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline"><Underline className="w-4 h-4" /></ToolbarButton>
        <div className={isStudio ? 'st31-div' : 'w-px h-5 bg-slate-300 mx-1'} />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet list"><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <div className={isStudio ? 'st31-div' : 'w-px h-5 bg-slate-300 mx-1'} />
        <ToolbarButton onClick={addLink} isActive={editor.isActive('link')} title="Add link"><Link2 className="w-4 h-4" /></ToolbarButton>
      </div>
      <style>{`
        .email-editor .ProseMirror { outline: none; min-height: 220px; padding: 16px; color: #0f172a; }
        html[data-design="studio"] .email-editor .ProseMirror { color: var(--st-fg); font-size: 14px; line-height: 1.55; padding: 14px 16px; }
        html[data-design="studio"] .email-editor .ProseMirror a { color: var(--st-acc); }
        .email-editor .ProseMirror h1 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0 .5rem; }
        .email-editor .ProseMirror h2 { font-size: 1.25rem; font-weight: 600; margin: 1rem 0 .5rem; }
        .email-editor .ProseMirror p { margin-bottom: .75rem; line-height: 1.5; }
        .email-editor .ProseMirror ul, .email-editor .ProseMirror ol { margin-bottom: .75rem; padding-left: 1.25rem; }
        .email-editor .ProseMirror a { color: #2563eb; text-decoration: underline; }
      `}</style>
      <div className="email-editor"><EditorContent editor={editor} /></div>
    </div>
  );
}

// ─────────────────────────────────────────── one email step
function EmailStepEditor({ step, index, stepNumber, flow, onChange, onRemove, canRemove, userEmail, unsavedSteps, senderMode }) {
  const { isStudio } = useDesign();
  const [mode, setMode] = useState(() => (needsHtmlMode(step.email?.body) ? 'html' : 'rich'));
  const richLocked = needsHtmlMode(step.email?.body);
  // The body can change under us (Reset, Restore): never leave the simple editor
  // showing a body it cannot round-trip.
  const activeMode = richLocked ? 'html' : mode;
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const bodyRef = useRef(null);

  const insertVariable = (name) => {
    const token = `{{${name}}}`;
    if (activeMode === 'html' && bodyRef.current) {
      const el = bodyRef.current;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const next = el.value.slice(0, start) + token + el.value.slice(end);
      onChange({ ...step, email: { ...step.email, body: next } });
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
    } else if (!navigator.clipboard) {
      toast.error(`Copy is unavailable here; type ${token} into the body`);
    } else {
      navigator.clipboard.writeText(token).then(() => toast.success(`Copied ${token}`)).catch(() => toast.error("Couldn't copy"));
    }
  };

  const switchMode = (next) => {
    if (next === 'rich' && richLocked) {
      toast.error('This email is laid out with tables and inline styling. The simple editor cannot keep that, so it stays in HTML.');
      return;
    }
    setMode(next);
  };

  const loadPreview = async () => {
    try {
      const data = await api(`/${flow.id}/preview`, { method: 'POST', userEmail, body: { steps: unsavedSteps, step_id: step.id, sender_mode: senderMode } });
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(/^Step \d/.test(e.message || '') ? `Fix this first: ${e.message}` : (e.message || "Couldn't render the preview"));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const data = await api(`/${flow.id}/test`, { method: 'POST', userEmail, body: { steps: unsavedSteps, step_id: step.id, sender_mode: senderMode } });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(/^Step \d/.test(e.message || '') ? `Fix this first: ${e.message}` : (e.message || "Couldn't send the test email"));
    } finally {
      setTesting(false);
    }
  };

  const recipientText = flow.is_sequence && step.recipient ? RECIPIENT_LABEL[step.recipient] || step.recipient : null;

  return (
    <div className={isStudio ? 'st31-step' : `${panel} overflow-hidden`}>
      <div className={isStudio ? 'st31-sh' : 'flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-700 bg-slate-800/80'}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={isStudio ? 'st31-g28' : 'w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0'}><Mail className={isStudio ? undefined : 'w-4 h-4 text-blue-300'} /></div>
          <div className="min-w-0">
            <p className={isStudio ? 'st31-t' : 'text-sm font-medium text-white'}>{flow.is_sequence ? `Email ${index + 1}` : 'The email'}{recipientText ? <span className={isStudio ? undefined : 'text-slate-400 font-normal'}> · to {recipientText}</span> : null}</p>
            <p className={isStudio ? 'st31-idl' : 'text-xs text-slate-500 truncate'}>Step {stepNumber} · id {step.id}</p>
          </div>
        </div>
        <div className={isStudio ? 'st31-r' : 'flex items-center gap-2'}>
          {isStudio ? (<>
            <button type="button" className="st-btn st-ghost st-sm" onClick={loadPreview} data-testid={`preview-${step.id}`}><Eye />Preview</button>
            <button type="button" className="st-btn st-ghost st-sm" onClick={sendTest} disabled={testing} data-testid={`test-${step.id}`}>
              {testing ? <Loader2 className="st31-spinning" /> : <Send />}{testing ? 'Sending…' : 'Send me a test'}
            </button>
            {canRemove && (
              <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={onRemove} title="Remove this reminder" aria-label="Remove this reminder"><Trash2 /></button>
            )}
          </>) : (<>
          <Button variant="outline" size="sm" className="border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white" onClick={loadPreview} data-testid={`preview-${step.id}`}><Eye className="w-4 h-4 mr-1.5" />Preview</Button>
          <Button variant="outline" size="sm" className="border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white" onClick={sendTest} disabled={testing} data-testid={`test-${step.id}`}><Send className="w-4 h-4 mr-1.5" />{testing ? 'Sending…' : 'Send me a test'}</Button>
          {canRemove && (
            <Button variant="ghost" size="sm" className="text-red-300 hover:text-red-200 hover:bg-red-500/10" onClick={onRemove} title="Remove this reminder"><Trash2 className="w-4 h-4" /></Button>
          )}
          </>)}
        </div>
      </div>

      <div className={isStudio ? 'st31-sb' : 'p-4 space-y-4'}>
        <div>
          <label className={isStudio ? labelSt : label}>Subject</label>
          <Input value={step.email?.subject || ''} onChange={(e) => onChange({ ...step, email: { ...step.email, subject: e.target.value } })}
            className={isStudio ? 'mt-1' : 'mt-1 bg-slate-900/60 border-slate-600 text-white'} placeholder="Subject line" data-testid={`subject-${step.id}`} />
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className={isStudio ? labelSt : label}>Body</label>
            <div className={isStudio ? 'st-segc' : 'flex items-center gap-1 text-xs'}>
              <button type="button" onClick={() => switchMode('rich')} disabled={richLocked}
                title={richLocked ? 'This email is laid out with tables and inline styling; the simple editor cannot keep it' : 'Simple editor'}
                className={isStudio
                  ? (activeMode === 'rich' ? 'st-on' : undefined)
                  : `px-2 py-1 rounded ${activeMode === 'rich' ? 'bg-slate-600 text-white' : richLocked ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white'}`}><Type className={isStudio ? undefined : 'w-3.5 h-3.5 inline mr-1'} />Simple</button>
              <button type="button" onClick={() => switchMode('html')} className={isStudio ? (activeMode === 'html' ? 'st-on' : undefined) : `px-2 py-1 rounded ${activeMode === 'html' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}><Code className={isStudio ? undefined : 'w-3.5 h-3.5 inline mr-1'} />HTML</button>
            </div>
          </div>
          <div className={isStudio ? 'st31-vars' : 'flex flex-wrap gap-1 mb-2'}>
            <span className={isStudio ? undefined : 'text-xs text-slate-500 self-center mr-1'}>{activeMode === 'html' ? 'Insert:' : 'Copy:'}</span>
            {(flow.variables || []).map((v) => (
              <button key={v} type="button" onClick={() => insertVariable(v)} title={flow.variable_help?.[v]?.label || v}
                className={isStudio ? 'st31-var' : 'px-2 py-0.5 text-[11px] font-mono rounded bg-blue-500/15 text-blue-200 hover:bg-blue-500/30 transition-colors'}>{`{{${v}}}`}</button>
            ))}
            {(flow.auto_variables || []).map((v) => (
              <button key={v} type="button" onClick={() => insertVariable(v)} title={`${flow.auto_variable_help?.[v]?.label || v} (available in every email)`}
                className={isStudio ? 'st31-var st31-auto' : 'px-2 py-0.5 text-[11px] font-mono rounded bg-slate-500/15 text-slate-300 hover:bg-slate-500/30 transition-colors'}>{`{{${v}}}`}</button>
            ))}
          </div>
          {activeMode === 'html' ? (
            <Textarea ref={bodyRef} value={step.email?.body || ''} onChange={(e) => onChange({ ...step, email: { ...step.email, body: e.target.value } })}
              className={isStudio ? 'font-mono text-xs min-h-[280px]' : 'font-mono text-xs min-h-[280px] bg-slate-900/60 border-slate-600 text-slate-100'} spellCheck={false} data-testid={`body-${step.id}`} />
          ) : (
            <RichEditor content={step.email?.body || ''} onChange={(html) => onChange({ ...step, email: { ...step.email, body: html } })} />
          )}
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle>Preview with example values</DialogTitle>
            <DialogDescription className="space-y-0.5">
              <span className="block"><strong>From:</strong> {preview?.from}</span>
              <span className="block"><strong>Subject:</strong> {preview?.subject}</span>
            </DialogDescription>
          </DialogHeader>
          {preview?.warnings?.length > 0 && (
            <div className={isStudio ? 'st-notice st-notice-warn' : 'rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex gap-2'}>
              <AlertTriangle className={isStudio ? undefined : 'w-4 h-4 flex-shrink-0 mt-0.5'} />
              <div>{preview.warnings.map((w, i) => <p key={i}>{w}</p>)}</div>
            </div>
          )}
          <div className={isStudio ? 'st31-preview' : 'border rounded-lg overflow-hidden bg-white'}>
            <iframe title="Email preview" sandbox="" srcDoc={preview?.html || ''} className="w-full h-[65vh] bg-white" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DelayStepEditor({ step, index, stepNumber, onChange }) {
  const { isStudio } = useDesign();
  const [text, setText] = useState(String(step.delay_days ?? 0));
  useEffect(() => { setText(String(step.delay_days ?? 0)); }, [step.delay_days]);
  const commit = () => {
    const n = Math.max(0, Math.min(90, parseInt(text, 10) || 0));
    if (String(n) !== text) setText(String(n));
    if (n !== (step.delay_days ?? 0)) onChange({ ...step, delay_days: n });
  };
  return (
    <div className={isStudio ? 'st31-delay' : 'flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10'}>
      {isStudio
        ? <Clock />
        : <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0"><Clock className="w-4 h-4 text-amber-300" /></div>}
      <label className={isStudio ? undefined : 'text-sm text-amber-100 flex items-center gap-2 flex-wrap'} style={isStudio ? { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } : undefined}>
        <span>Wait</span>
        <Input type="number" min={0} max={90} value={text}
          onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
          className={isStudio ? 'w-20 h-8 text-center' : 'w-20 h-8 bg-slate-900/60 border-amber-500/40 text-white text-center'} data-testid={`delay-${step.id}`} />
        <span>day{(step.delay_days ?? 0) === 1 ? '' : 's'}{index === 0 ? ' after it starts' : ' after the previous email'}</span>
      </label>
      <span className={isStudio ? 'st31-r' : 'ml-auto text-[11px] text-amber-300/70 whitespace-nowrap'}>Step {stepNumber}</span>
    </div>
  );
}

// ─────────────────────────────────────────── history dialog
function HistoryDialog({ open, onOpenChange, flow, userEmail, onRestored, dirty }) {
  const { isStudio } = useDesign();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api(`/${flow.id}/versions`, { userEmail })
      .then((d) => setVersions(d.versions || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [open, flow.id, userEmail]);
  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v.version}? The last saved copy goes to History first${dirty ? '; unsaved edits in the editor will be lost' : ''}.`)) return;
    try {
      const d = await api(`/${flow.id}/versions/${v.version}/restore`, { method: 'POST', userEmail });
      toast.success(d.message);
      (d.warnings || []).forEach((w) => toast.warning(w));
      onRestored(d.flow);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>History</DialogTitle>
          <DialogDescription>Every save keeps the previous copy. Restoring never loses the current one.</DialogDescription>
        </DialogHeader>
        {loading ? (isStudio ? (
          <div className="st31-skstat">
            {[0, 1, 2].map(i => <span key={i} className="st31-sk" style={{ height: 14, width: `${70 - i * 8}%` }} />)}
          </div>
        ) : <p className="text-sm text-slate-500">Loading…</p>) : versions.length === 0 ? (
          isStudio
            ? <div className="st31-quiet"><b>No earlier versions yet</b><span>Every save keeps the copy it replaced.</span></div>
            : <p className="text-sm text-slate-500">No earlier versions yet.</p>
        ) : (
          <ul className={isStudio ? 'st31-stack' : 'divide-y'} style={isStudio ? { listStyle: 'none', margin: 0, padding: 0, gap: 0 } : undefined}>
            {versions.map((v) => (
              <li key={v.version} className={isStudio ? 'st31-arow' : 'py-3 flex items-start justify-between gap-3'} style={isStudio ? { padding: '12px 0' } : undefined}>
                <div className={isStudio ? 'st31-c' : 'min-w-0'}>
                  <p className={isStudio ? 'st31-t' : 'text-sm font-medium'}>Version {v.version} <span className={isStudio ? undefined : 'text-slate-500 font-normal'} style={isStudio ? { color: 'var(--st-fg2)', fontWeight: 400 } : undefined}>· {v.reason}</span></p>
                  <p className={isStudio ? 'st31-m' : 'text-xs text-slate-500'}>{new Date(v.saved_at).toLocaleString()} · {v.saved_by}</p>
                  <p className={isStudio ? 'st31-m' : 'text-xs text-slate-500 truncate'}>Subject then: {(v.snapshot?.steps || []).find((s) => s.type === 'email')?.email?.subject || '—'}</p>
                </div>
                {isStudio
                  ? <button type="button" className="st-btn st-sec st-sm" onClick={() => restore(v)}>Restore</button>
                  : <Button size="sm" variant="outline" onClick={() => restore(v)}>Restore</Button>}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────── detail view
function FlowDetailView({ flow: initial, onBack, onSaved, userEmail }) {
  const { isStudio } = useDesign();
  const [flow, setFlow] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copied, setCopied] = useState(null);

  const edit = (patch) => { setFlow((f) => ({ ...f, ...patch })); setDirty(true); };
  const editStep = (index, step) => edit({ steps: flow.steps.map((s, i) => (i === index ? step : s)) });
  const removeReminder = (index) => {
    if (!window.confirm('Remove this reminder and the wait before it?')) return;
    // remove the email and the wait right before it
    const steps = [...flow.steps];
    steps.splice(index, 1);
    if (index > 0 && steps[index - 1]?.type === 'delay') steps.splice(index - 1, 1);
    edit({ steps });
  };
  const addReminder = () => {
    const emails = flow.steps.filter((s) => s.type === 'email');
    // Seed from the FIRST email, not the last: the last step of the invitation
    // sequence goes to the admin, and copying it would send the escalation twice.
    const seedRecipient = Object.keys(flow.recipients || {})[0];
    const seed = emails.find((e) => !e.recipient || e.recipient === seedRecipient) || emails[0];
    const ids = new Set(flow.steps.map((s) => s.id));
    let n = emails.length + 1;
    while (ids.has(`wait_${n}`) || ids.has(`send_reminder_${n}`)) n += 1;
    edit({ steps: [...flow.steps,
      { id: `wait_${n}`, type: 'delay', delay_days: 4 },
      { id: `send_reminder_${n}`, type: 'email', recipient: seed?.recipient || seedRecipient,
        email: { subject: seed?.email?.subject || '', body: seed?.email?.body || '' } },
    ] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await api(`/${flow.id}`, { method: 'PUT', userEmail, body: {
        name: flow.name, description: flow.description, enabled: flow.enabled, sender_mode: flow.sender_mode, steps: flow.steps,
      } });
      setFlow(d.flow); setDirty(false); onSaved(d.flow);
      toast.success(d.message || 'Saved');
      (d.warnings || []).forEach((w) => toast.warning(w, { duration: 8000 }));
    } catch (e) {
      toast.error(e.message || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (saving) return;
    if (!window.confirm(`Reset this email to the Worship OS default? The last saved copy goes to History first${dirty ? '; your unsaved edits will be lost' : ''}.`)) return;
    setSaving(true);
    try {
      const d = await api(`/${flow.id}/reset`, { method: 'POST', userEmail });
      setFlow(d.flow); setDirty(false); onSaved(d.flow); toast.success(d.message);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const back = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    onBack();
  };

  const copyVar = (v) => {
    if (!navigator.clipboard) { toast.error(`Copy is unavailable here; type {{${v}}} into the body`); return; }
    navigator.clipboard.writeText(`{{${v}}}`).then(() => { setCopied(v); setTimeout(() => setCopied(null), 1200); }).catch(() => toast.error("Couldn't copy"));
  };

  const schedule = flow.is_sequence ? scheduleSummary(flow) : [];
  const emailSteps = flow.steps.filter((s) => s.type === 'email').length;

  return (
    <div className={isStudio ? shellSt : shell}>
      {isStudio ? (
        <StudioToolHeader
          icon={Mail}
          title={flow.name}
          titleExtra={<>
            <StatusChip enabled={flow.enabled} />
            {!flow.is_default && <span className="st-chip st-tb-xs">Customised</span>}
          </>}
          sub={flow.category_label}
          backLabel="All emails"
          onBack={back}
          right={<>
            {dirty && <span className="st31-saveline"><AlertTriangle />Unsaved changes</span>}
            <button type="button" className="st-btn st-sec st-sm" onClick={() => setHistoryOpen(true)}><History />History</button>
            <button type="button" className="st-btn st-ghost st-sm" onClick={reset} disabled={saving || flow.is_default}><RotateCcw />Reset to default</button>
            <button type="button" className="st-btn st-pri st-sm" onClick={save} disabled={saving || !dirty} data-testid="save-flow"><Save />{saving ? 'Saving…' : 'Save'}</button>
          </>}
        />
      ) : (
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={back} className="text-slate-300 hover:text-white hover:bg-slate-700"><ArrowLeft className="w-4 h-4 mr-2" />All emails</Button>
            <div className="h-6 w-px bg-slate-600" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-white truncate">{flow.name}</h1>
                <StatusChip enabled={flow.enabled} />
                {!flow.is_default && <Badge variant="outline" className="text-violet-300 border-violet-400/40">Customised</Badge>}
              </div>
              <p className="text-xs text-slate-400 truncate">{flow.category_label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} className="border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"><History className="w-4 h-4 mr-2" />History</Button>
            <Button variant="outline" size="sm" onClick={reset} className="border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white" disabled={saving || flow.is_default}><RotateCcw className="w-4 h-4 mr-2" />Reset</Button>
            <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-blue-600 hover:bg-blue-700" data-testid="save-flow"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </header>
      )}

      <div className={isStudio ? 'st31-pg st31-two' : 'max-w-6xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6'}>
        <div className={isStudio ? 'st31-stack' : 'space-y-5 min-w-0'}>
          {/* Name + description + switch */}
          <div className={isStudio ? 'st31-card' : `${panel} p-4 space-y-3`}>
            {isStudio && <div className="st31-ch"><h2 className="st31-ct">This email flow</h2></div>}
            <div className={isStudio ? 'st31-cb' : undefined}>
            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start">
              <div className="space-y-3">
                <div>
                  <label className={isStudio ? labelSt : label}>Name (shown only here)</label>
                  <Input value={flow.name} onChange={(e) => edit({ name: e.target.value })} className={isStudio ? 'mt-1' : 'mt-1 bg-slate-900/60 border-slate-600 text-white'} />
                </div>
                <div>
                  <label className={isStudio ? labelSt : label}>Description (shown only here)</label>
                  <Input value={flow.description || ''} onChange={(e) => edit({ description: e.target.value })} className={isStudio ? 'mt-1' : 'mt-1 bg-slate-900/60 border-slate-600 text-white'} />
                </div>
              </div>
              <div className={isStudio ? 'st31-htile' : 'rounded-lg border border-slate-700 p-3 min-w-[200px]'} style={isStudio ? { minWidth: 200 } : undefined}>
                <div className="flex items-center justify-between gap-3">
                  <span className={isStudio ? undefined : 'text-sm text-white'}>{flow.enabled ? 'Sending' : 'Switched off'}</span>
                  <Switch checked={!!flow.enabled} onCheckedChange={(v) => edit({ enabled: v })} data-testid="flow-enabled" />
                </div>
                <p className={isStudio ? 'st31-s' : 'text-xs text-slate-400 mt-2'}>{flow.enabled ? 'Off stops this email for every church.' : 'Nothing is sent, and nothing is marked as sent, until this is on again.'}</p>
              </div>
            </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className={isStudio ? 'st31-ct' : 'text-sm font-semibold text-white'}>{flow.is_sequence ? `Sequence · ${emailSteps} email${emailSteps === 1 ? '' : 's'}` : 'Content'}</h2>
              {flow.is_sequence && emailSteps < 10 && (isStudio ? (
                <button type="button" className="st-btn st-ghost st-sm" onClick={addReminder}><Plus />Add another reminder</button>
              ) : (
                <Button size="sm" variant="outline" onClick={addReminder} className="border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"><Plus className="w-4 h-4 mr-1.5" />Add another reminder</Button>
              ))}
            </div>
            {flow.is_sequence && flow.sequence && (
              <p className={isStudio ? 'st31-cap' : 'text-xs text-slate-400'}>Counting from {flow.sequence.anchor}. Each email goes out at {flow.sequence.time}, only while {flow.sequence.condition}.</p>
            )}
            {flow.steps.map((step, index) => {
              if (step.type === 'delay') return <DelayStepEditor key={step.id} step={step} index={index} stepNumber={index + 1} onChange={(s) => editStep(index, s)} />;
              if (step.type === 'email') {
                return (
                  <EmailStepEditor key={step.id} step={step} index={flow.steps.slice(0, index).filter((s) => s.type === 'email').length} stepNumber={index + 1} flow={flow}
                    onChange={(s) => editStep(index, s)} onRemove={() => removeReminder(index)} canRemove={flow.is_sequence && emailSteps > 1}
                    userEmail={userEmail} unsavedSteps={flow.steps} senderMode={flow.sender_mode} />
                );
              }
              return (
                <div key={step.id} className={isStudio ? 'st31-cond' : 'px-4 py-3 rounded-xl border border-slate-700 text-sm text-slate-400'}>Only if {String(step.condition || '').replace(/_/g, ' ')}</div>
              );
            })}
          </div>
        </div>

        {/* Facts panel */}
        <aside className={isStudio ? 'st31-stack' : 'space-y-4'}>
          <div className={isStudio ? 'st31-card' : `${panel} p-4 space-y-4`}>
            {isStudio && <div className="st31-ch"><h2 className="st31-ct">When it is sent</h2></div>}
            <div className={isStudio ? 'st31-cb st31-kvl' : undefined}>
            <div>
              {!isStudio && <p className={label}>When it is sent</p>}
              {isStudio && <label className="st31-lbl">Trigger</label>}
              <p className={isStudio ? undefined : 'text-sm text-slate-200 mt-1'}>{flow.trigger}</p>
              {isStudio
                ? <span className="st-chip st-tb-xs" style={{ marginTop: 4 }}>{TRIGGER_LABEL[flow.trigger_type] || flow.trigger_type}</span>
                : <Badge variant="outline" className="mt-2 text-slate-300 border-slate-600">{TRIGGER_LABEL[flow.trigger_type] || flow.trigger_type}</Badge>}
            </div>
            {flow.is_sequence && schedule.length > 0 && (
              <div>
                <p className={isStudio ? labelSt : label}>Schedule (from the waits above)</p>
                <ul className={isStudio ? undefined : 'mt-1 space-y-1 text-sm text-slate-200'}>
                  {schedule.map((s, i) => <li key={i}>Day {s.day}: email {i + 1}{s.recipient && s.recipient !== Object.keys(flow.recipients || {})[0] ? ` to ${RECIPIENT_LABEL[s.recipient] || s.recipient}` : ''}</li>)}
                </ul>
              </div>
            )}
            <div>
              <p className={isStudio ? labelSt : label}>Who receives it</p>
              <p className={isStudio ? undefined : 'text-sm text-slate-200 mt-1 flex items-start gap-2'} style={isStudio ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}><Users className={isStudio ? undefined : 'w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0'} style={isStudio ? { width: 16, height: 16, color: 'var(--st-fg2)', flex: 'none' } : undefined} />{flow.sends_to}</p>
            </div>
            <div>
              <p className={isStudio ? labelSt : label}>Sent as</p>
              {flow.sender_editable ? (
                <select value={flow.sender_mode} onChange={(e) => edit({ sender_mode: e.target.value })}
                  className={isStudio ? 'st31-input' : 'mt-1 w-full text-sm rounded-md bg-slate-900 border border-slate-600 text-white px-2 py-1.5 [&>option]:bg-slate-900 [&>option]:text-white'}>
                  {Object.entries(flow.sender_options || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              ) : (
                <p className={isStudio ? undefined : 'text-sm text-slate-200 mt-1'}>{flow.sender_options?.[flow.sender_mode] || flow.sender_mode}</p>
              )}
            </div>
            {flow.appends?.length > 0 && (
              <div>
                <p className={isStudio ? labelSt : label}>Added automatically</p>
                <ul className={isStudio ? undefined : 'mt-1 text-sm text-slate-300 list-disc pl-4'}>{flow.appends.map((a) => <li key={a}>{a}</li>)}</ul>
              </div>
            )}
            {flow.layout === 'bare' ? (
              <p className={isStudio ? 'st31-cap' : 'text-xs text-slate-400'}>Sent without the Worship OS header and footer.</p>
            ) : (
              <p className={isStudio ? 'st31-cap' : 'text-xs text-slate-400'}>Sent inside the Worship OS header and footer. Preview shows the whole thing.</p>
            )}
            </div>
          </div>

          <div className={isStudio ? 'st31-card' : `${panel} p-4`}>
            {isStudio && <div className="st31-ch"><h2 className="st31-ct">Variables</h2></div>}
            <div className={isStudio ? 'st31-cb' : undefined}>
            {!isStudio && <p className={label}>Variables</p>}
            <p className={isStudio ? 'st31-cap' : 'text-xs text-slate-400 mt-1 mb-3'}>Filled in for each church at send time. Anything else typed as {'{{…}}'} goes out literally.</p>
            <ul className={isStudio ? 'st31-varlist' : 'space-y-2'}>
              {(flow.variables || []).map((v) => (
                <li key={v} className={isStudio ? undefined : 'text-xs'}>
                  <button type="button" onClick={() => copyVar(v)} className={isStudio ? undefined : 'font-mono text-blue-200 hover:text-white inline-flex items-center gap-1'} title="Copy">
                    {`{{${v}}}`}{copied === v
                      ? <Check className={isStudio ? undefined : 'w-3 h-3 text-emerald-300'} style={isStudio ? { color: 'var(--st-ok)' } : undefined} />
                      : <Copy className={isStudio ? undefined : 'w-3 h-3 opacity-60'} />}
                  </button>
                  <p className={isStudio ? undefined : 'text-slate-400'}>{flow.variable_help?.[v]?.label}{flow.variable_help?.[v]?.example ? <span className={isStudio ? undefined : 'text-slate-500'}> · e.g. {String(flow.variable_help[v].example).slice(0, 60)}</span> : null}</p>
                </li>
              ))}
            </ul>
            </div>
          </div>
        </aside>
      </div>

      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} flow={flow} userEmail={userEmail} dirty={dirty} onRestored={(f) => { setFlow(f); setDirty(false); onSaved(f); }} />
    </div>
  );
}

// ─────────────────────────────────────────── list view
export function EmailFlowsManager({ userEmail, onClose }) {
  const { isStudio } = useDesign();
  const [flows, setFlows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [elsewhere, setElsewhere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    if (!userEmail) { setError('User email not available'); setLoading(false); return; }
    try {
      const d = await api('', { userEmail });
      setFlows(d.flows || []); setCategories(d.categories || []); setElsewhere(d.managed_elsewhere || []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load email flows');
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flows;
    return flows.filter((f) => [f.name, f.description, f.trigger, f.sends_to, f.id].some((t) => (t || '').toLowerCase().includes(q)));
  }, [flows, query]);

  const selected = flows.find((f) => f.id === selectedId);
  if (selected) {
    return <FlowDetailView key={selected.id} flow={selected} onBack={() => setSelectedId(null)} userEmail={userEmail}
      onSaved={(f) => setFlows((all) => all.map((x) => (x.id === f.id ? f : x)))} />;
  }

  const onCount = flows.filter((f) => f.enabled).length;
  const customised = flows.filter((f) => !f.is_default).length;

  return (
    <div className={isStudio ? shellSt : shell}>
      {isStudio ? (
        <StudioToolHeader
          icon={Mail}
          title="Email flows"
          sub="Every email Worship OS sends. Edits here change the base email for every church."
          backLabel="Dashboard"
          onBack={onClose}
          search={<ToolSearch value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search emails" />}
        />
      ) : (
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-300 hover:text-white hover:bg-slate-700"><ArrowLeft className="w-4 h-4 mr-2" />Dashboard</Button>
            <div className="h-6 w-px bg-slate-600" />
            <div>
              <h1 className="text-lg font-bold text-white">Email Flows</h1>
              <p className="text-xs text-slate-400">Every email Worship OS sends. Edits here change the base email for every church.</p>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-2.5" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search emails" className="pl-8 w-64 bg-slate-900/60 border-slate-600 text-white" />
          </div>
        </div>
      </header>
      )}

      <div className={isStudio ? 'st31-pg' : 'max-w-6xl mx-auto p-6 space-y-8'}>
        {loading ? (isStudio ? (
          <div className="st31-card"><div className="st31-cb">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="st31-skrow" style={{ height: 56 }}>
                <span className="st31-sk" style={{ width: 36, height: 36, borderRadius: 10 }} />
                <span className="st31-skcol">
                  <span className="st31-sk" style={{ width: '46%', height: 14 }} />
                  <span className="st31-sk" style={{ width: '70%', height: 12 }} />
                </span>
              </div>
            ))}
          </div></div>
        ) : (
          <div className="flex items-center justify-center h-64 text-slate-400">Loading email flows…</div>
        )) : error ? (isStudio ? (
          <div className="st-notice st-notice-bad"><AlertTriangle /><div><b>Couldn&rsquo;t load the email flows.</b> {error}</div></div>
        ) : (
          <div className="flex items-center justify-center h-64 text-red-400">{error}</div>
        )) : (
          <>
            <div className={isStudio ? 'st31-summary' : 'flex flex-wrap gap-4 text-sm text-slate-300'}>
              <span><strong className={isStudio ? undefined : 'text-white'}>{flows.reduce((n, f) => n + emailCount(f), 0)}</strong> emails across {flows.length} templates</span>
              <span><strong className={isStudio ? undefined : 'text-white'}>{onCount}</strong> on</span>
              <span><strong className={isStudio ? undefined : 'text-white'}>{customised}</strong> customised</span>
            </div>

            {categories.map((cat) => {
              const rows = visible.filter((f) => f.category === cat.id);
              if (rows.length === 0) return null;
              return (
                <section key={cat.id}>
                  {isStudio
                    ? <div className="st31-lblsec">{cat.label}<span>{rows.length} {rows.length === 1 ? 'email' : 'emails'}</span></div>
                    : <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{cat.label}</h2>}
                  <div className={isStudio ? 'st31-card' : 'grid gap-2'}>
                    {rows.map((flow) => {
                      const n = emailCount(flow);
                      const sched = flow.is_sequence ? scheduleSummary(flow) : [];
                      return (
                        <button type="button" key={flow.id} onClick={() => setSelectedId(flow.id)} data-testid={`flow-${flow.id}`}
                          className={isStudio ? 'st31-flowrow' : `${panel} text-left px-4 py-3 hover:border-slate-500 transition-colors flex items-center gap-4`}>
                          <div className={isStudio ? 'st31-g36' : `w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${flow.enabled ? 'bg-slate-700' : 'bg-red-500/10'}`}>
                            {flow.is_sequence
                              ? <Clock className={isStudio ? undefined : `w-5 h-5 ${flow.enabled ? 'text-slate-300' : 'text-red-300'}`} />
                              : <Mail className={isStudio ? undefined : `w-5 h-5 ${flow.enabled ? 'text-slate-300' : 'text-red-300'}`} />}
                          </div>
                          <div className={isStudio ? 'st31-c' : 'min-w-0 flex-1'}>
                            <div className={isStudio ? 'st31-nm' : 'flex items-center gap-2 flex-wrap'}>
                              <h3 className={isStudio ? undefined : 'font-semibold text-white'} style={isStudio ? { margin: 0, font: 'inherit' } : undefined}>{flow.name}</h3>
                              <StatusChip enabled={flow.enabled} />
                              {!flow.is_default && (isStudio
                                ? <span className="st-chip st-tb-xs">Customised</span>
                                : <Badge variant="outline" className="text-violet-300 border-violet-400/40">Customised</Badge>)}
                            </div>
                            <p className={isStudio ? 'st31-tr' : 'text-sm text-slate-400 mt-0.5'}>{flow.trigger}</p>
                            <p className={isStudio ? 'st31-mt' : 'text-xs text-slate-500 mt-1'}>
                              To: {AUDIENCE_LABEL[flow.audience] || flow.sends_to}
                              {flow.is_sequence ? ` · ${n} email${n === 1 ? '' : 's'}, day ${sched.map((s) => s.day).join(' / ')}` : ''}
                              {flow.sender_mode === 'via' ? ' · sent as the admin via Worship OS' : flow.sender_mode === 'direct' ? ' · sent as the admin' : ' · sent as Worship OS'}
                            </p>
                          </div>
                          <ChevronRight className={isStudio ? undefined : 'w-5 h-5 text-slate-500 flex-shrink-0'} />
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            {elsewhere.length > 0 && !query && (
              <section>
                {isStudio
                  ? <div className="st31-lblsec">Managed elsewhere<span>not editable here</span></div>
                  : <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Managed elsewhere (not editable here)</h2>}
                <div className={isStudio ? 'st31-card' : 'grid gap-2'}>
                  {elsewhere.map((row) => (
                    <div key={row.name} className={isStudio ? 'st31-flowrow' : `${panel} px-4 py-3 flex items-center gap-4`}>
                      <div className={isStudio ? 'st31-g36' : 'w-10 h-10 rounded-lg bg-slate-700/60 flex items-center justify-center flex-shrink-0'}><ExternalLink className={isStudio ? undefined : 'w-5 h-5 text-slate-400'} /></div>
                      <div className={isStudio ? 'st31-c' : 'min-w-0'}>
                        <h3 className={isStudio ? 'st31-nm' : 'font-semibold text-slate-200'} style={isStudio ? { margin: 0 } : undefined}>{row.name}</h3>
                        <p className={isStudio ? 'st31-tr' : 'text-sm text-slate-400'}>{row.trigger}</p>
                        <p className={isStudio ? 'st31-mt' : 'text-xs text-slate-500 mt-1'}>Edit in: {row.where}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default EmailFlowsManager;
